import {
    EVisibility,
    EWorldMapMarkerType,
    WorldMapMarkerDataDirectionInput,
    WorldMapMarkerDataDrawInput,
    WorldMapMarkerDataImageInput,
    WorldMapMarkerDataLinkInput,
    WorldMapMarkerDataPinInput,
    WorldMapMarkerDataPolygonInput,
    WorldMapMarkerDataReactInput,
    WorldMapMarkerDataRouteInput,
    WorldMapMarkerDataTextInput,
    WorldMapMarkerDataYoutubeInput,
    WorldMapMarkerInput
} from '__generated__/globalTypes';
import { WorldMapMarkerStory_worldMapView_markers } from 'graphql/query/__generated__/WorldMapMarkerStory';
import { omitDeep } from 'libs/utils/object/objectOperations';
import { createRef } from 'react';
import {
    EMapStyle,
    IDirectionWorldMarker,
    IDrawWorld,
    IReactWorld,
    IWorldMarker,
    WorldElements
} from 'react-give-me-map';

export const mapGraphQLMarker = (m: WorldMapMarkerStory_worldMapView_markers): IWorldMarker => {
    const baseProps = {
        id: m.worldMapMarkerId,
        selectable: !m.locked,
        scalable: m.scalable,
        layerTitle: m.layerTitle,
        visible: m.visible,
        isGroupable: m.isGroupable,
        gptContextId: m.gptContextId,
        isRoomMarker: m.isRoomMarker,
        roomName: m.roomName,
        isExternalPoint: m.isExternalPoint,
        storyIds: m.stories.map((e) => e.storyId),
        ...m
    };

    if (m.data.__typename === 'WorldMapMarkerDataPin') {
        return {
            ...baseProps,
            elementType: 'pin' as const,
            elementData: m.data,
            ref: createRef<HTMLDivElement>()
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataImage') {
        return {
            ...baseProps,
            elementType: 'image' as const,
            elementData: {
                ...omitDeep(m.data, ['__typename']),
                // TODO
                additionalSrc: undefined
            },
            ref: createRef<HTMLDivElement>()
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataDirection') {
        return {
            ...baseProps,
            elementType: 'direction' as const,
            elementData: {
                ...omitDeep(m.data, ['__typename']),
                transport: m.data.transport as IDirectionWorldMarker['elementData']['transport']
            },
            refs: []
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataDraw') {
        return {
            ...baseProps,
            elementType: 'draw' as const,
            elementData: {
                ...omitDeep(m.data, ['__typename']),
                type: m.data.type as IDrawWorld['type'],
                path: m.data.stringPath,
                fill: m.data.isFilled
            },
            ref: createRef<HTMLDivElement>()
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataLink') {
        return {
            ...baseProps,
            elementType: 'link' as const,
            elementData: {
                ...omitDeep(m.data, ['__typename'])
            },
            ref: createRef<HTMLDivElement>()
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataText') {
        return {
            ...baseProps,
            elementType: 'text' as const,
            elementData: {
                ...omitDeep(m.data, ['__typename']),
                fill: m.data.isFilled ?? false
            },
            ref: createRef<HTMLDivElement>()
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataYoutube') {
        return {
            ...baseProps,
            elementType: 'youtube' as const,
            elementData: {
                ...omitDeep(m.data, ['__typename'])
            },
            ref: createRef<HTMLDivElement>()
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataRoute') {
        return {
            ...baseProps,
            elementType: 'route' as const,
            elementData: {
                ...omitDeep(m.data, ['__typename'])
            },
            ref: createRef<HTMLDivElement>()
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataPolygon') {
        return {
            ...baseProps,
            elementType: 'polygon' as const,
            elementData: {
                ...omitDeep(m.data, ['__typename']),
                path: m.data.stringPath,
                fill: m.data.isFilled
            },
            ref: createRef<HTMLDivElement>()
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataReact') {
        const elementData: IReactWorld = {
            componentTypeId: m.data.componentTypeId,
            componentData: Object.fromEntries(m.data.componentData.map((d) => [d.name, d.value]))
        };
        return {
            ...baseProps,
            elementType: 'react',
            elementData,
            ref: createRef<HTMLDivElement>()
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataLineV2') {
        return {
            ...baseProps,
            elementType: 'v2/line',
            elementData: omitDeep(m.data, ['__typename'])
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataPolygonV2') {
        return {
            ...baseProps,
            elementType: 'v2/polygon',
            elementData: omitDeep(m.data, ['__typename'])
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataIconV2') {
        return {
            ...baseProps,
            elementType: 'v2/icon',
            elementData: omitDeep(m.data, ['__typename'])
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataVariantIconV2') {
        return {
            ...baseProps,
            elementType: 'v2/variant_icon',
            elementData: omitDeep(m.data, ['__typename'])
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataTextV2') {
        return {
            ...baseProps,
            elementType: 'v2/text',
            elementData: omitDeep(m.data, ['__typename'])
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataWallV2') {
        return {
            ...baseProps,
            elementType: 'v2/wall',
            elementData: omitDeep(m.data, ['__typename'])
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataImageV2') {
        return {
            ...baseProps,
            elementType: 'v2/image',
            elementData: omitDeep(m.data, ['__typename'])
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataRouteV2') {
        return {
            ...baseProps,
            elementType: 'v2/route',
            elementData: omitDeep(m.data, ['__typename'])
        };
    }
    if (m.data.__typename === 'WorldMapMarkerDataIndoorStand') {
        return {
            ...baseProps,
            elementType: 'indoor_stand',
            elementData: omitDeep(m.data, ['__typename'])
        };
    }
};

export const mapGraphQLMarkers = (
    markers: WorldMapMarkerStory_worldMapView_markers[]
): IWorldMarker[] => {
    const result = markers.map<IWorldMarker>(mapGraphQLMarker);

    return result.filter(Boolean);
};

export const visibilityToGraphQL = (visibility: 'public' | 'private'): EVisibility => {
    if (visibility === 'public') {
        return EVisibility.PUBLIC;
    }
    return EVisibility.PRIVATE;
};

const markerTypeToGraphQL = (markerType: WorldElements): EWorldMapMarkerType => {
    switch (markerType) {
        case 'direction':
            return EWorldMapMarkerType.DIRECTION;
        case 'draw':
            return EWorldMapMarkerType.DRAW;
        case 'image':
            return EWorldMapMarkerType.IMAGE;
        case 'link':
            return EWorldMapMarkerType.LINK;
        case 'pin':
            return EWorldMapMarkerType.PIN;
        case 'polygon':
            return EWorldMapMarkerType.POLYGON;
        case 'route':
            return EWorldMapMarkerType.ROUTE;
        case 'text':
            return EWorldMapMarkerType.TEXT;
        case 'youtube':
            return EWorldMapMarkerType.YOUTUBE;
        case 'react':
            return EWorldMapMarkerType.REACT;
        case 'v2/line':
            return EWorldMapMarkerType.V2_LINE;
        case 'v2/polygon':
            return EWorldMapMarkerType.V2_POLYGON;
        case 'v2/icon':
            return EWorldMapMarkerType.V2_ICON;
        case 'v2/variant_icon':
            return EWorldMapMarkerType.V2_VARIANT_ICON;
        case 'v2/text':
            return EWorldMapMarkerType.V2_TEXT;
        case 'v2/wall':
            return EWorldMapMarkerType.V2_WALL;
        case 'v2/image':
            return EWorldMapMarkerType.V2_IMAGE;
        case 'v2/route':
            return EWorldMapMarkerType.V2_ROUTE;
        case 'indoor_stand':
            return EWorldMapMarkerType.INDOOR_STAND;
    }

    throw Error(`Unsupported markerType ${markerType}`);
};

export const markerDataToGraphQL = (marker: IWorldMarker): WorldMapMarkerInput => {
    return {
        data: {
            pin:
                marker.elementType === 'pin'
                    ? ({
                          label: marker.elementData.label,
                          img: marker.elementData.img,
                          emoji: marker.elementData.emoji,
                          dotColor: marker.elementData.dotColor,
                          icon: marker.elementData.icon
                              ? {
                                    iconText: marker.elementData.icon.iconText,
                                    iconColor: marker.elementData.icon.iconColor,
                                    backgroundColor: marker.elementData.icon.backgroundColor
                                }
                              : null
                      } as WorldMapMarkerDataPinInput)
                    : {},
            youtube:
                marker.elementType === 'youtube'
                    ? ({
                          video: marker.elementData.video,
                          borderRadiusPx: marker.elementData.borderRadiusPx,
                          borderSize: marker.elementData.borderSize,
                          borderColor: marker.elementData.borderColor,
                          dropShadowCombined: marker.elementData.dropShadowCombined
                      } as WorldMapMarkerDataYoutubeInput)
                    : {},
            text:
                marker.elementType === 'text'
                    ? ({
                          text: marker.elementData.text,
                          width: marker.elementData.width,
                          fill: marker.elementData.fill,
                          color: marker.elementData.color,
                          borderRadiusPx: marker.elementData.borderRadiusPx,
                          borderSize: marker.elementData.borderSize,
                          borderColor: marker.elementData.borderColor,
                          dropShadowCombined: marker.elementData.dropShadowCombined,
                          textColor: marker.elementData.textColor,
                          textShadow: marker.elementData.textShadow
                      } as WorldMapMarkerDataTextInput)
                    : {},
            image:
                marker.elementType === 'image'
                    ? ({
                          src: marker.elementData.src,
                          srcWidth: marker.elementData.srcWidth,
                          borderRadiusPx: marker.elementData.borderRadiusPx,
                          borderSize: marker.elementData.borderSize,
                          borderColor: marker.elementData.borderColor,
                          dropShadowCombined: marker.elementData.dropShadowCombined,
                          renderAsLayer: marker.elementData.renderAsLayer,
                          layerData: {
                              topLeft: marker.elementData.layerData?.topLeft,
                              topRight: marker.elementData.layerData?.topRight,
                              bottomLeft: marker.elementData.layerData?.bottomLeft,
                              bottomRight: marker.elementData.layerData?.bottomRight
                          }
                      } as WorldMapMarkerDataImageInput)
                    : {},
            route:
                marker.elementType === 'route'
                    ? ({
                          coordinates: marker.elementData.coordinates,
                          fillColor: marker.elementData.fillColor,
                          fillColorOpacity: marker.elementData.fillColorOpacity,
                          lineColor: marker.elementData.lineColor,
                          lineOpacity: marker.elementData.lineOpacity,
                          dropShadowColor: marker.elementData.dropShadowColor
                      } as WorldMapMarkerDataRouteInput)
                    : {},
            draw:
                marker.elementType === 'draw'
                    ? ({
                          type: marker.elementData.type,
                          path: marker.elementData.path,
                          width: marker.elementData.width,
                          height: marker.elementData.height,
                          opacity: marker.elementData.opacity,
                          strokeWidth: marker.elementData.strokeWidth,
                          color: marker.elementData.color,
                          fill: marker.elementData.fill
                      } as WorldMapMarkerDataDrawInput)
                    : {},
            polygon:
                marker.elementType === 'polygon'
                    ? ({
                          path: marker.elementData.path,
                          coordinates: marker.elementData.coordinates,
                          width: marker.elementData.width,
                          height: marker.elementData.height,
                          color: marker.elementData.color,
                          fill: marker.elementData.fill,
                          renderAs3d: marker.elementData.renderAs3d,
                          data3d: {
                              level: marker.elementData.data3d?.level,
                              height: marker.elementData.data3d?.height,
                              baseHeight: marker.elementData.data3d?.baseHeight,
                              color: marker.elementData.data3d?.color
                          }
                      } as WorldMapMarkerDataPolygonInput)
                    : {},
            direction:
                marker.elementType === 'direction'
                    ? ({
                          coordinates: marker.elementData.coordinates,
                          transport: marker.elementData.transport,
                          width: marker.elementData.width,
                          path: marker.elementData.path,
                          color: marker.elementData.color,
                          opacity: marker.elementData.opacity
                      } as WorldMapMarkerDataDirectionInput)
                    : {},
            link:
                marker.elementType === 'link'
                    ? ({
                          link: marker.elementData.link,
                          text: marker.elementData.text,
                          width: marker.elementData.width,
                          color: marker.elementData.color,
                          borderRadiusPx: marker.elementData.borderRadiusPx,
                          borderSize: marker.elementData.borderSize,
                          borderColor: marker.elementData.borderColor,
                          dropShadowCombined: marker.elementData.dropShadowCombined
                      } as WorldMapMarkerDataLinkInput)
                    : {},
            react:
                marker.elementType === 'react'
                    ? ({
                          componentTypeId: marker.elementData.componentTypeId,
                          componentData: Object.entries(marker.elementData.componentData).map(
                              (e) => ({ name: e[0], value: e[1] })
                          )
                      } as WorldMapMarkerDataReactInput)
                    : {},
            lineV2:
                marker.elementType === 'v2/line'
                    ? {
                          coordinates: marker.elementData.coordinates,
                          color: marker.elementData.color,
                          width: marker.elementData.width,
                          opacity: marker.elementData.opacity,
                          dashed: marker.elementData.dashed,
                          rawPaintAttributes: marker.elementData.rawPaintAttributes,
                          rawLayoutAttributes: marker.elementData.rawLayoutAttributes
                      }
                    : {},
            polygonV2:
                marker.elementType === 'v2/polygon'
                    ? {
                          coordinates: marker.elementData.coordinates,
                          fill: {
                              isFilled: marker.elementData.fill.isFilled,
                              color: marker.elementData.fill.color,
                              opacity: marker.elementData.fill.opacity,
                              rawPaintAttributes: marker.elementData.fill.rawPaintAttributes,
                              rawLayoutAttributes: marker.elementData.fill.rawLayoutAttributes
                          },
                          border: {
                              hasBorder: marker.elementData.border.hasBorder,
                              color: marker.elementData.border.color,
                              width: marker.elementData.border.width,
                              opacity: marker.elementData.border.opacity,
                              rawPaintAttributes: marker.elementData.border.rawPaintAttributes,
                              rawLayoutAttributes: marker.elementData.border.rawLayoutAttributes
                          }
                      }
                    : {},
            iconV2:
                marker.elementType === 'v2/icon'
                    ? {
                          imageUrl: marker.elementData.imageUrl,
                          text: marker.elementData.text,
                          imageSize: marker.elementData.imageSize,
                          textSize: marker.elementData.textSize,
                          textColor: marker.elementData.textColor,
                          textHaloColor: marker.elementData.textHaloColor,
                          textHaloWidth: marker.elementData.textHaloWidth,
                          generatedBackgroundColor: marker.elementData.generatedBackgroundColor,
                          generatedIconUrl: marker.elementData.generatedIconUrl,
                          generatedImageUrl: marker.elementData.generatedImageUrl,
                          generatedEmoji: marker.elementData.generatedEmoji,
                          generatedIconColor: marker.elementData.generatedIconColor,
                          rawPaintAttributes: marker.elementData.rawPaintAttributes,
                          rawLayoutAttributes: marker.elementData.rawLayoutAttributes
                      }
                    : {},
            variantIconV2:
                marker.elementType === 'v2/variant_icon'
                    ? {
                          imageUrl: marker.elementData.imageUrl,
                          text: marker.elementData.text,
                          color: marker.elementData.color,
                          useStoryImage: marker.elementData.useStoryImage,
                          eventDate: marker.elementData.eventDate,
                          circleText: marker.elementData.circleText,
                          withStar: marker.elementData.withStar
                      }
                    : {},
            textV2:
                marker.elementType === 'v2/text'
                    ? {
                          imageUrl: marker.elementData.imageUrl,
                          coordinates: marker.elementData.coordinates,
                          nonRotatedCoordinates: marker.elementData.nonRotatedCoordinates,
                          rotation: marker.elementData.rotation,
                          generatedText: marker.elementData.generatedText,
                          generatedTextColor: marker.elementData.generatedTextColor,
                          generatedBackgroundColor: marker.elementData.generatedBackgroundColor,
                          generatedWidth: marker.elementData.generatedWidth,
                          lineLength: marker.elementData.lineLength,
                          rawPaintAttributes: marker.elementData.rawPaintAttributes,
                          rawLayoutAttributes: marker.elementData.rawLayoutAttributes
                      }
                    : {},
            imageV2:
                marker.elementType === 'v2/image'
                    ? {
                          imageUrl: marker.elementData.imageUrl,
                          coordinates: marker.elementData.coordinates,
                          nonRotatedCoordinates: marker.elementData.nonRotatedCoordinates,
                          rotation: marker.elementData.rotation,
                          opacity: marker.elementData.opacity,
                          flipHorizontal: marker.elementData.flipHorizontal,
                          flipVertical: marker.elementData.flipVertical,
                          generatedBackgroundColor: marker.elementData.generatedBackgroundColor,
                          generatedImageUrl: marker.elementData.generatedImageUrl,
                          generatedIconUrl: marker.elementData.generatedIconUrl,
                          generatedEmoji: marker.elementData.generatedEmoji,
                          generatedIconColor: marker.elementData.generatedIconColor
                      }
                    : {},
            wallV2:
                marker.elementType === 'v2/wall'
                    ? {
                          coordinates: marker.elementData.coordinates,
                          nonTransformedCoordinates: marker.elementData.nonTransformedCoordinates,
                          rotation: marker.elementData.rotation,
                          scale: marker.elementData.scale,
                          label: marker.elementData.label,
                          wall: {
                              color: marker.elementData.wall.color,
                              height: marker.elementData.wall.height,
                              baseHeight: marker.elementData.wall.baseHeight,
                              opacity: marker.elementData.wall.opacity
                          },
                          line: {
                              isLine: marker.elementData.line.isLine,
                              hasFloor: marker.elementData.line.hasFloor,
                              fillColor: marker.elementData.line.fillColor,
                              width: marker.elementData.line.width
                          }
                      }
                    : {},
            routeV2:
                marker.elementType === 'v2/route'
                    ? {
                          coordinates: marker.elementData.coordinates,
                          color: marker.elementData.color,
                          width: marker.elementData.width,
                          opacity: marker.elementData.opacity,
                          dashed: marker.elementData.dashed
                      }
                    : {},
            indoorStand:
                marker.elementType === 'indoor_stand'
                    ? {
                          text: marker.elementData.text,
                          textColor: marker.elementData.textColor,
                          textHaloColor: marker.elementData.textHaloColor,
                          textHaloWidth: marker.elementData.textHaloWidth,
                          pinSrc: marker.elementData.pinSrc,
                          imageSrc: marker.elementData.imageSrc,
                          pinGeneratedType: marker.elementData.pinGeneratedType,
                          pinGeneratedImageUrl: marker.elementData.pinGeneratedImageUrl,
                          pinGeneratedIconUrl: marker.elementData.pinGeneratedIconUrl,
                          pinGeneratedIconColor: marker.elementData.pinGeneratedIconColor,
                          logoGeneratedImageUrl: marker.elementData.logoGeneratedImageUrl,
                          logoGeneratedStyleId: marker.elementData.logoGeneratedStyleId
                      }
                    : {}
        }
    };
};

export const markerToGraphQL = (marker: IWorldMarker): WorldMapMarkerInput => {
    return {
        lat: marker.lat,
        lng: marker.lng,
        placeId: marker.placeId,
        placeText: marker.placeText,
        scale: marker.scale,
        locked: !marker.selectable,
        layerTitle: marker.layerTitle,
        scalable: marker.scalable,
        rotate: marker.rotate,
        order: marker.order,
        visible: marker.visible,
        isGroupable: marker.isGroupable,
        gptContextId: marker.gptContextId,
        isRoomMarker: marker.isRoomMarker,
        roomName: marker.roomName,
        storyIds: marker.storyIds,
        type: markerTypeToGraphQL(marker.elementType),
        data: markerDataToGraphQL(marker).data
    };
};

export const GraphQLStylesMapToEnum = {
    default: EMapStyle.DEFAULT,
    outdoor: EMapStyle.OUTDOOR,
    satellite: EMapStyle.SATELLITE
};
